import React from 'react';
import { motion } from 'framer-motion';
import { WhyMe } from '../../data/data';

interface WhyMeSectionProps {
  data: WhyMe;
}

const WhyMeSection: React.FC<WhyMeSectionProps> = ({ data }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="why-me" className="section-padding bg-white">
      <div className="container-max">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2 variants={itemVariants} className="text-4xl font-bold text-gray-900 mb-4">
            {data.title}
          </motion.h2>
          <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-3xl mx-auto text-left text-justify">
            {data.description}
          </motion.p>
        </motion.div>
        {data.cards.map((card, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            whileHover={{ y: -8, scale: 1.02 }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden group mb-8"
          >
            {/* Decorative background element */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-edata-blue/5 to-edata-light-blue/5 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform duration-500"></div>
            
            {/* Icon container */}
            <div className="relative z-10 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-edata-blue to-edata-light-blue rounded-xl flex items-center justify-center shadow-lg">
                <div className="text-4xl">{card.icon}</div>
              </div>
            </div>

            <div className="relative z-10">
              <h3 className="text-xl font-bold text-gray-900 mb-4 leading-tight">{card.title}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{card.description}</p>
              
              <div className="space-y-3">
                {card.listItems.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-gradient-to-r from-edata-blue to-edata-light-blue rounded-full mt-2"></div>
                    <p className="text-gray-700 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default WhyMeSection; 